/**
 * UserMenu Component
 *
 * User avatar with dropdown menu showing user info and logout button.
 * Click avatar to toggle the yellow dropdown overlay.
 *
 * Features:
 * - Avatar image from user photo or fallback icon
 * - Yellow dropdown overlay on click
 * - User name/email display
 * - Logout button
 * - Click outside to close
 * - Keyboard accessible (Escape to close)
 *
 * Ported from Vue UserAvatar.vue
 *
 * @example
 * ```tsx
 * <UserMenu />
 * ```
 */

import { useAuth } from "@/hooks/auth/useAuth";
import { useUser } from "@/hooks/auth/useUser";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Avatar } from "@/components/ui/display/Avatar";
import { Icon } from "@/components/ui/display/Icon";
import { cx } from "@/utils/cx";
import type { UserMenuProps } from "./types";
import styles from "./UserMenu.module.css";

export const UserMenu: React.FC<UserMenuProps> = ({
  className = "",
  "data-testid": testId,
}) => {
  const [showMenu, setShowMenu] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const { displayName, email, photo } = useUser();
  const { logout } = useAuth();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showMenu]);

  // Close menu on Escape key
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setShowMenu(false);
      }
    };

    if (showMenu) {
      document.addEventListener("keydown", handleKeyDown);
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [showMenu]);

  // Toggle menu
  const handleToggle = useCallback(() => {
    setShowMenu((prev) => !prev);
  }, []);

  // Handle logout
  const handleLogout = useCallback(() => {
    setShowMenu(false);
    logout();
  }, [logout]);

  // Build class names
  const containerClasses = cx(styles.container, className);
  const overlayClasses = cx(
    styles.overlayContainer,
    !showMenu && styles.hidden,
  );

  return (
    <div ref={containerRef} className={containerClasses} data-testid={testId}>
      <Avatar
        src={photo}
        name={displayName}
        clickable
        onClick={handleToggle}
        aria-expanded={showMenu}
        size="lg"
        aria-haspopup="true"
        aria-label="User menu"
        data-testid="auth-user-menu"
      />

      {/* Dropdown Overlay - slides from behind the avatar button */}
      <div
        className={overlayClasses}
        role="menu"
        aria-hidden={!showMenu}
        inert={!showMenu ? true : undefined}
      >
        <div className={styles.overlay}>
          {/* User info */}
          <div className={styles.userInfo}>
            {displayName && (
              <span className={styles.userName} data-testid="auth-user-name">
                {displayName}
              </span>
            )}
            {email && (
              <span className={styles.userEmail} data-testid="auth-user-email">
                {email}
              </span>
            )}
          </div>

          {/* Logout button */}
          <button
            type="button"
            className={styles.logoutButton}
            onClick={handleLogout}
            role="menuitem"
            data-testid="auth-logout-button"
          >
            <Icon name="logout" size="sm" aria-hidden={true} />
            Logg ut
          </button>
        </div>
      </div>
    </div>
  );
};

UserMenu.displayName = "UserMenu";
